import { IIntegrationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { DropdownInlineEditor } from 'components/inline_edit/DropdownInlineEditor';
import { NoValue } from 'components/no_value/NoValue';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { useNotificationContext } from 'contexts/NotificationContext';
import { useIntegrationSettings } from 'custom_hooks/data_hooks/integrations/useIntegrationSettings';
import {
    DetailGrid,
    DetailName,
    DetailValue,
    Header,
} from 'pages/Settings/Integrations/styled';
import { AuthenticationDetailsContent } from 'pages/Settings/Integrations/tabs/AuthenticationDetailsContent';
import { IntegrationDetailsInlineEditor } from 'pages/Settings/Integrations/tabs/IntegrationDetailsInlineEditor';
import {
    AuthenticationType,
    AuthenticationTypeOptions,
    IntegrationDetails,
} from 'pages/Settings/Integrations/types';
import { PropsWithChildren, ReactNode, useMemo } from 'react';
import { updateIntegration } from 'utils/integration';

export type IntegrationSettingsTabProps = Readonly<{
    locationId: string;
    integration: IIntegrationResponse;
}>;

export const DetailRow = ({
    name,
    children,
}: PropsWithChildren<{ name: string | ReactNode }>) => (
    <>
        <DetailName>{name}</DetailName>
        <DetailValue>{children}</DetailValue>
    </>
);

export function IntegrationSettingsTab({
    locationId,
    integration,
}: IntegrationSettingsTabProps) {
    const { showToast } = useNotificationContext();
    const { data, isLoading, refetch } = useIntegrationSettings(locationId, integration);
    const options = useMemo(() => AuthenticationTypeOptions.map((c) => c.value), []);

    const { mutate: updateIntegrationSettings, isPending: isUpdating } = useMutation({
        meta: { name: 'update-integration' },
        mutationFn: (update: Partial<IntegrationDetails>) => {
            const payload: IntegrationDetails = {
                ...update,
                systemid: integration.SoftwareName,
            };
            return updateIntegration({
                locationId,
                integration,
                updatedConfiguration: payload,
            });
        },

        onSuccess: () => {
            refetch();
            showToast({
                type: 'success',
                content: `Successfully updated ${integration.SoftwareName} settings`,
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: `Failed to update ${integration.SoftwareName} settings`,
            }),
    });

    const onSaveAuthType = (value: AuthenticationType | undefined) => {
        updateIntegrationSettings({
            authtype: value,
            tokenurl: '',
            secret: '',
            id: '',
        });
        refetch();
    };

    const onSaveUrl = (value: string | undefined) =>
        updateIntegrationSettings({
            base_url: value,
        });

    const render = (value: AuthenticationType | undefined) => {
        if (!value) {
            return <NoValue />;
        }
        return (
            AuthenticationTypeOptions.find((option) => option.value === value)?.title ?? (
                <NoValue />
            )
        );
    };

    const isDisabled = isLoading || isUpdating;

    return (
        <SpinnerArea isLoading={isDisabled}>
            {data && (
                <>
                    <DetailGrid className="mb-15">
                        <IntegrationDetailsInlineEditor
                            name="Integration URL"
                            value={data.base_url}
                            isDisabled={isDisabled}
                            onConfirm={onSaveUrl}
                            type="url"
                        />
                    </DetailGrid>
                    <Header>Authentication Details</Header>
                    <DetailGrid>
                        <DetailRow name="Type of Authentication">
                            <DropdownInlineEditor
                                value={data.authtype}
                                renderValue={render}
                                options={options}
                                isDisabled={isDisabled}
                                onConfirm={onSaveAuthType}
                                dropdownConfig={{
                                    renderItem: ({ item }) => render(item),
                                    renderLabel: ({ value }) => render(value),
                                    dataKey: (item) => item,
                                }}
                            />
                        </DetailRow>
                        <AuthenticationDetailsContent
                            data={data}
                            onConfirm={updateIntegrationSettings}
                            isDisabled={isDisabled}
                        />
                    </DetailGrid>
                </>
            )}
        </SpinnerArea>
    );
}
